import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { SubmitButton } from "../shared/submit-button.js";
import { useTranslation } from "react-i18next";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { DifficultyBadge, DebugQuizId } from "../shared/debug-quiz-id.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useBooleanQuiz } from "../hooks/use-boolean-quiz.js";
import Lottie from "../../../node_modules/.pnpm/lottie-react@2.4.1_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/lottie-react/build/index.es.js";
import quedaAnim from "../boolean/assets/queda-ox-60.json.js";
import dotAnim from "../boolean/assets/dot.json.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
const SEG_START = 0;
const SEG_MID = 30;
const SEG_END = 60;
function BooleanStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel
}) {
  const { t } = useTranslation("quiz");
  const [hoverO, setHoverO] = useState(false);
  const [hoverX, setHoverX] = useState(false);
  const lottieRef = useRef(null);
  const mountedRef = useRef(false);
  const posRef = useRef("center");
  const {
    selected,
    isSolved,
    canSubmit,
    correctAnswer,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSelect,
    handleSubmit
  } = useBooleanQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const isInteractive = !isSolved && !isViewSolved;
  useEffect(() => {
    var _a;
    (_a = lottieRef.current) == null ? void 0 : _a.goToAndStop(SEG_MID, true);
    requestAnimationFrame(() => {
      mountedRef.current = true;
    });
  }, []);
  useEffect(() => {
    const anim = lottieRef.current;
    if (!anim || !mountedRef.current || selected !== null) return;
    if (hoverO) {
      anim.playSegments([SEG_MID, SEG_START], true);
      posRef.current = "left";
    } else {
      anim.playSegments([SEG_START, SEG_MID], true);
      posRef.current = "center";
    }
  }, [hoverO]);
  useEffect(() => {
    const anim = lottieRef.current;
    if (!anim || !mountedRef.current || selected !== null) return;
    if (hoverX) {
      anim.playSegments([SEG_MID, SEG_END], true);
      posRef.current = "right";
    } else {
      anim.playSegments([SEG_END, SEG_MID], true);
      posRef.current = "center";
    }
  }, [hoverX]);
  useEffect(() => {
    const anim = lottieRef.current;
    if (!anim || selected === null) return;
    if (selected === true) {
      if (posRef.current === "left") return;
      anim.playSegments([SEG_END, SEG_START], true);
      posRef.current = "left";
    } else {
      if (posRef.current === "right") return;
      anim.playSegments([SEG_START, SEG_END], true);
      posRef.current = "right";
    }
  }, [selected]);
  const oIsSelected = selected === true;
  const oIsCorrectResult = isSolved && correctAnswer === true && (shouldShowCorrectAnswers || selected === true);
  const oIsWrongResult = isSolved && selected === true && correctAnswer === false && !shouldShowCorrectAnswers;
  const xIsSelected = selected === false;
  const xIsCorrectResult = isSolved && correctAnswer === false && (shouldShowCorrectAnswers || selected === false);
  const xIsWrongResult = isSolved && selected === false && correctAnswer === true && !shouldShowCorrectAnswers;
  const oButtonClass = cn(
    "w-[88px] h-[88px] rounded-full cursor-pointer select-none transition-all duration-150",
    "active:translate-y-2 active:[box-shadow:unset] active:border-b-0",
    (isSolved || isViewSolved) && "pointer-events-none",
    oIsCorrectResult && "bg-[#3BD0AE] [box-shadow:unset] translate-y-2",
    oIsWrongResult && "bg-[#FF675E] [box-shadow:unset] translate-y-2",
    !isSolved && oIsSelected && "bg-[#FFAE03] [box-shadow:unset] translate-y-2",
    isSolved && !oIsCorrectResult && !oIsWrongResult && !oIsSelected && "bg-[#E0E0E0] [box-shadow:0px_6px_0px_#BBBBBB,0px_12px_0px_rgba(16,16,16,0.1)]",
    !isSolved && !oIsSelected && "bg-[#55C3FF] [box-shadow:0px_6px_0px_#2E96CE,0px_12px_0px_rgba(16,16,16,0.1)] hover:bg-[#99DBFF] hover:[box-shadow:0px_6px_0px_#55c3ff,0px_12px_0px_rgba(16,16,16,0.1)]"
  );
  const xButtonClass = cn(
    "w-[88px] h-[88px] rounded-full cursor-pointer select-none transition-all duration-150",
    "active:translate-y-2 active:[box-shadow:unset] active:border-b-0",
    (isSolved || isViewSolved) && "pointer-events-none",
    xIsCorrectResult && "bg-[#3BD0AE] [box-shadow:unset] translate-y-2",
    xIsWrongResult && "bg-[#FF675E] [box-shadow:unset] translate-y-2",
    !isSolved && xIsSelected && "bg-[#FFAE03] [box-shadow:unset] translate-y-2",
    isSolved && !xIsCorrectResult && !xIsWrongResult && !xIsSelected && "bg-[#E0E0E0] [box-shadow:0px_6px_0px_#BBBBBB,0px_12px_0px_rgba(16,16,16,0.1)]",
    !isSolved && !xIsSelected && "bg-[#55C3FF] [box-shadow:0px_6px_0px_#2E96CE,0px_12px_0px_rgba(16,16,16,0.1)] hover:bg-[#99DBFF] hover:[box-shadow:0px_6px_0px_#55c3ff,0px_12px_0px_rgba(16,16,16,0.1)]"
  );
  const completed = isSolved;
  const centerBgClass = cn(
    "w-[162px] h-[88px] flex items-center justify-center rounded-3xl relative z-10",
    !completed && selected === null && "bg-[#DDF3FF]",
    !completed && selected !== null && "bg-[#FFEFCD]",
    !showReallyTrue && completed && (solveResult == null ? void 0 : solveResult.correct) === false && "bg-[#FFE1DF]",
    (showReallyTrue || completed && (solveResult == null ? void 0 : solveResult.correct) === true) && "bg-[#D8F6EF]"
  );
  const renderCenterText = () => {
    if (showReallyTrue) {
      return /* @__PURE__ */ jsx("span", { className: "font-bold text-[32px] text-[#3BD0AE]", children: "정답" });
    }
    const showTrue = (selected === null || selected === false) && hoverO || selected === true && !hoverX;
    const showFalse = (selected === null || selected === true) && hoverX || selected === false && !hoverO;
    if (showTrue) {
      const textColor = cn(
        "font-bold text-[32px]",
        hoverO && selected === null && "text-[#55C3FF]",
        (selected === true || hoverO) && !completed && "text-[#FFAE03]",
        completed && (solveResult == null ? void 0 : solveResult.correct) === true && "text-[#3BD0AE]"
      );
      return /* @__PURE__ */ jsx("span", { className: textColor, children: t("types.boolean.correct") });
    }
    if (showFalse) {
      const textColor = cn(
        "font-bold text-[32px]",
        hoverX && selected === null && "text-[#55C3FF]",
        (selected === false || hoverX) && !completed && "text-[#FFAE03]",
        completed && (solveResult == null ? void 0 : solveResult.correct) === false && "text-[#FF675E]"
      );
      return /* @__PURE__ */ jsx("span", { className: textColor, children: t("types.boolean.incorrect") });
    }
    return /* @__PURE__ */ jsx(Lottie, { animationData: dotAnim, loop: true, autoplay: true, style: { width: 55, height: 10 } });
  };
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center", children: [
          /* @__PURE__ */ jsx("div", { className: "inline-flex gap-2 items-center px-3 py-1 text-sm font-bold text-white bg-[#55C3FF] rounded-lg", children: /* @__PURE__ */ jsx("span", { children: t("types.boolean.label") }) }),
          /* @__PURE__ */ jsx(DifficultyBadge, { level: difficultyLevel }),
          /* @__PURE__ */ jsx(DebugQuizId, { quizId })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: 0.1 },
        className: cn(
          "p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50",
          "border border-gray-100 shadow-2xs",
          "text-2xl font-medium leading-relaxed text-gray-900"
        ),
        children: [
          /* @__PURE__ */ jsx(KatexText, { text: quiz.title }),
          quiz.problem && /* @__PURE__ */ jsx("div", { className: "mt-4 text-xl", children: formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? void 0) }),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "flex gap-10 justify-center items-end mt-8",
        role: "radiogroup",
        "aria-label": t("types.boolean.selectLabel"),
        children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              role: "radio",
              "aria-checked": oIsSelected,
              onClick: () => isInteractive && handleSelect(true),
              onMouseEnter: () => isInteractive && !oIsSelected && setHoverO(true),
              onMouseLeave: () => setHoverO(false),
              className: oButtonClass,
              "aria-disabled": !isInteractive,
              children: /* @__PURE__ */ jsx(
                "svg",
                {
                  width: "44",
                  height: "44",
                  viewBox: "0 0 44 44",
                  fill: "none",
                  xmlns: "http://www.w3.org/2000/svg",
                  className: "mx-auto",
                  children: /* @__PURE__ */ jsx("circle", { cx: "22", cy: "22", r: "14", stroke: "white", strokeWidth: "5", fill: "none" })
                }
              )
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col justify-center items-center text-gray-700", children: [
            /* @__PURE__ */ jsx("div", { className: "-mb-[64px] z-0", children: /* @__PURE__ */ jsx(
              Lottie,
              {
                lottieRef,
                animationData: quedaAnim,
                loop: false,
                autoplay: false,
                style: { width: 100, height: 150 }
              }
            ) }),
            /* @__PURE__ */ jsx("div", { className: centerBgClass, children: renderCenterText() })
          ] }),
          /* @__PURE__ */ jsx(
            "button",
            {
              role: "radio",
              "aria-checked": xIsSelected,
              onClick: () => isInteractive && handleSelect(false),
              onMouseEnter: () => isInteractive && !xIsSelected && setHoverX(true),
              onMouseLeave: () => setHoverX(false),
              className: xButtonClass,
              "aria-disabled": !isInteractive,
              children: /* @__PURE__ */ jsxs(
                "svg",
                {
                  width: "44",
                  height: "44",
                  viewBox: "0 0 44 44",
                  fill: "none",
                  xmlns: "http://www.w3.org/2000/svg",
                  className: "mx-auto",
                  children: [
                    /* @__PURE__ */ jsx("line", { x1: "12", y1: "12", x2: "32", y2: "32", stroke: "white", strokeWidth: "5", strokeLinecap: "round" }),
                    /* @__PURE__ */ jsx("line", { x1: "32", y1: "12", x2: "12", y2: "32", stroke: "white", strokeWidth: "5", strokeLinecap: "round" })
                  ]
                }
              )
            }
          )
        ]
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers ? correctAnswer ? t("types.boolean.correct") : t("types.boolean.incorrect") : void 0,
        disableSound
      }
    ),
    isSolved && (solveResult == null ? void 0 : solveResult.description) && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: solveResult.description,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !solveResult.correct,
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  BooleanStudent
};
//# sourceMappingURL=boolean-student.js.map
